import { useMemo, useState } from 'react';

import { Breadcrumb } from '@components/breadcrumb';
import { Button } from '@components/button';
import { useLocalStorageState } from '@components/component-helpers';
import { EmptySearchState } from '@components/empty-search-state';
import { EmptyState } from '@components/empty-state';
import { ExpandableSearch } from '@components/expandable-search';
import { useDeleteModal } from '@components/generic-modals';
import { Header } from '@components/header';
import { IconDuplicate } from '@components/icons/duplicate';
import { IconEdit } from '@components/icons/edit';
import { IconIntegration } from '@components/icons/integration';
import { IconSortAsc } from '@components/icons/sort-asc';
import { IconSortDesc } from '@components/icons/sort-desc';
import { IconTrash } from '@components/icons/trash';
import { ListCard } from '@components/list-card';
import { Spinner } from '@components/loading-spinner';
import { Menu } from '@components/menu';
import type { MenuItemProps } from '@components/menu/menu';
import { PageContent } from '@components/page-containers/page-container';
import { SquareImage } from '@components/square-image';
import { Stack } from '@components/stack';
import { ValueList } from '@components/value-list';
import { useScopedTranslate } from '@locale/translate';
import { toQueryString } from '@utils/requests';

import { useDeleteIntegration, useIntegrationList } from './integrations-hooks';
import type { Integration } from './integrations-types';
import { useDuplicateIntegrationModal } from './modals/duplicate-integration';
import { useIntegrationAddEditSidebar } from './sidebars/add-edit-integration';
import { AuthenticationStatus } from './sidebars/authentication';

const orderingItems = [
  'name',
  '-name',
  '-date_created',
  'date_created',
  '-date_updated',
  'date_updated',
] as const;

type IntegrationListProps = {
  breadcrumbLabel: string;
  onIntegrationCreated?: (integration: Integration) => void;
  renderIntegrationIcon?: (integration: Integration) => React.ReactNode;
  renderIntegrationActions?: (integration: Integration) => Array<MenuItemProps | 'separator'>;
  getIntegrationUrl?: (integration: Integration) => string;
  emptyStateContent: string | JSX.Element;
};

export const IntegrationList = ({
  breadcrumbLabel,
  onIntegrationCreated,
  renderIntegrationIcon,
  renderIntegrationActions,
  getIntegrationUrl,
  emptyStateContent,
}: IntegrationListProps) => {
  const { t } = useScopedTranslate(locale);
  const [searchStr, setSearchStr] = useState('');
  const [ordering, setOrdering] = useLocalStorageState<(typeof orderingItems)[number]>({
    key: 'integration-list-order',
    defaultValue: '-date_created',
  });
  const queryString = useMemo(() => {
    return toQueryString({
      ...(searchStr && { search: searchStr }),
      ordering,
    });
  }, [searchStr, ordering]);

  const integrationsQuery = useIntegrationList({ queryString });
  const showAddEditSidebar = useIntegrationAddEditSidebar();
  const showDeleteModal = useDeleteModal();
  const showDuplicateModal = useDuplicateIntegrationModal();
  const deleteMutation = useDeleteIntegration();

  if (!integrationsQuery.data) {
    return <Spinner />;
  }

  const integrations = integrationsQuery.data;
  const isDataEmpty = !integrations || integrations.length === 0;
  const isDescendingOrdering = ordering.startsWith('-');

  const defaultRenderIcon = (integration: Integration) => {
    return integration.thumbnail ? (
      <SquareImage src={integration.thumbnail} />
    ) : (
      <IconIntegration />
    );
  };

  return (
    <>
      <Header
        breadcrumbs={<Breadcrumb items={[{ label: breadcrumbLabel }]} />}
        actions={
          <Stack space={2} orientation="horizontal">
            <ExpandableSearch onChange={setSearchStr} />
            <Menu
              maxWidth="auto"
              width="auto"
              buttonProps={{
                variant: 'secondary',
                text: t(`sort_${ordering}`).split(' • ')[0],
                iconLeft: isDescendingOrdering ? <IconSortDesc /> : <IconSortAsc />,
              }}
            >
              <Menu.Label label={t('sort')} />
              {orderingItems.map((item) => (
                <Menu.Item
                  key={item}
                  onSelect={() => setOrdering(item)}
                  isActive={ordering === item}
                  label={t(`sort_${item}`)}
                />
              ))}
            </Menu>
            <Button
              onClick={() =>
                showAddEditSidebar({
                  onCreated: onIntegrationCreated,
                })
              }
              text={t('addIntegration')}
            />
          </Stack>
        }
      />
      <PageContent>
        {isDataEmpty ? (
          searchStr ? (
            <EmptySearchState skipMargin />
          ) : (
            <EmptyState
              skipMargin
              icon={<IconIntegration size={80} />}
              header={t('noIntegrationsTitle')}
              content={emptyStateContent}
            />
          )
        ) : (
          <Stack space={2}>
            {integrations.map((integration) => {
              const integrationUrl = getIntegrationUrl?.(integration);

              return (
                <ListCard
                  key={integration.shortId}
                  to={integrationUrl}
                  spaceX={6}
                  spaceY={4}
                  title={integration.name}
                  description={integration.description}
                  details={<AuthenticationStatus integration={integration} />}
                  icon={(renderIntegrationIcon ?? defaultRenderIcon)(integration)}
                  actionItems={
                    renderIntegrationActions
                      ? renderIntegrationActions(integration)
                      : [
                          {
                            icon: <IconEdit />,
                            label: t('core.edit'),
                            onSelect: () => showAddEditSidebar({ integration }),
                          },
                          {
                            icon: <IconDuplicate />,
                            label: t('duplicate'),
                            onSelect: () => showDuplicateModal({ integration }),
                          },
                          'separator',
                          {
                            icon: <IconTrash />,
                            label: t('deleteIntegration'),
                            variant: 'danger',
                            onSelect: () =>
                              showDeleteModal({
                                onConfirm: () => deleteMutation.mutateAsync(integration.shortId),
                                title: t('deleteIntegration'),
                                hasUserConfirmation: true,
                                description: t('deleteDescription', {
                                  itemTitle: integration.name,
                                }),
                              }),
                          },
                        ]
                  }
                  actions={
                    <ValueList
                      items={[
                        { value: integration.numJourneys, label: t('numberOfJourneys') },
                        {
                          value: integration.endpoints.length + integration.webhooks.length,
                          label: t('numberOfEndpoints'),
                        },
                      ]}
                    />
                  }
                />
              );
            })}
          </Stack>
        )}
      </PageContent>
    </>
  );
};

const locale = {
  en: {
    addIntegration: 'Add new integration',
    deleteIntegration: 'Delete integration',
    deleteDescription:
      'Are you sure you want to delete {itemTitle}?\nDeleting this integration might impact your existing workflows and synced lists. Are you sure you want to continue?',
    noIntegrationsTitle: 'No custom integrations',
    aboutIntegrations: 'Learn more about integrations',
    noIntegrationsDescription: 'Create a custom connector to connect to a REST API.',
    numberOfJourneys: '# Journeys',
    numberOfEndpoints: '# Endpoints',
    sort: 'Sort by',
    sort_name: 'Alphabetical ascending • A → Z',
    'sort_-name': 'Alphabetical descending • Z → A',
    'sort_-date_created': 'Created • Newest',
    sort_date_created: 'Created • Oldest',
    'sort_-date_updated': 'Last edit • New → Old',
    sort_date_updated: 'Last edit • Old → New',
    duplicate: 'Duplicate',
  },
};
